/**
 * IndexedDB image cache — stores raw Blobs keyed by session/filename.
 * Falls back gracefully if IndexedDB is unavailable.
 */

const DB_NAME = 'slidetoanki-images';
const DB_VERSION = 1;
const STORE_NAME = 'images';
const MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours

interface CachedImage {
  key: string;          // "{sessionId}/{filename}"
  blob: Blob;
  timestamp: number;
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'key' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/** Get a cached image as an Object URL. Returns null if not cached or expired. */
export async function getCachedImage(sessionId: string, filename: string): Promise<string | null> {
  try {
    const db = await openDB();
    const key = `${sessionId}/${filename}`;

    return new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const req = store.get(key);

      req.onsuccess = () => {
        const entry = req.result as CachedImage | undefined;
        if (!entry || Date.now() - entry.timestamp > MAX_AGE_MS) {
          resolve(null);
          return;
        }
        resolve(URL.createObjectURL(entry.blob));
      };
      req.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}

/** Store an image blob in the cache. */
export async function cacheImage(sessionId: string, filename: string, blob: Blob): Promise<void> {
  try {
    const db = await openDB();
    const key = `${sessionId}/${filename}`;

    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      store.put({ key, blob, timestamp: Date.now() } as CachedImage);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    // Silent fail — cache is best-effort
  }
}

/** Fetch an image from the API and cache it. Returns an Object URL. */
export async function fetchAndCache(apiUrl: string, sessionId: string, filename: string): Promise<string> {
  // Use frontend proxy to avoid WebSocket connection pool blocking
  // The proxy at /api/images/* forwards to the backend
  const url = `/api/images/${sessionId}/${filename}`;

  const res = await fetch(url);
  if (!res.ok) throw new Error(`Image fetch failed: ${res.status}`);

  const blob = await res.blob();
  await cacheImage(sessionId, filename, blob);

  return URL.createObjectURL(blob);
}

/**
 * Get an image — cache-first, then network.
 * Returns an Object URL ready for <img src>.
 */
export async function getImage(apiUrl: string, sessionId: string, filename: string): Promise<string> {
  // Try cache first
  const cached = await getCachedImage(sessionId, filename);
  if (cached) return cached;

  // Fetch from API and cache for next time
  return fetchAndCache(apiUrl, sessionId, filename);
}

/** Purge expired entries. Call occasionally (e.g., on app mount). */
export async function purgeExpired(): Promise<void> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const req = store.openCursor();

    req.onsuccess = () => {
      const cursor = req.result;
      if (!cursor) return;
      const entry = cursor.value as CachedImage;
      if (Date.now() - entry.timestamp > MAX_AGE_MS) {
        cursor.delete();
      }
      cursor.continue();
    };
  } catch {
    // Silent fail
  }
}
